import axios from "axios";
import { formatISO } from "date-fns";
import Decimal from "decimal.js";
import { orderBy } from "lodash";
import { z } from "zod";
import { environment } from "../../../environment";
import { errorLog } from "../../../logger";
import { ClientId, CustodianAccountId, PaginationMetadata, PhysicalAccountId, SecurityId, } from "../../Types";
import ValidationError from "../../ZodError";
import { ConcreteImplementationPlanIntent, ConcreteImplementationPlanStatus, TradeProposal, TradeProposalBundleId, TradeProposalBundleStatus, } from "../types";
export const MiniTrade = z.object({
    physicalAccountId: PhysicalAccountId,
    custodianAccountId: CustodianAccountId.nullable(),
    securityId: SecurityId,
    quantity: z.string().transform((v) => new Decimal(v)),
    side: z.enum(["Buy", "Sell"]),
    orderType: z.literal("Market"),
    price: z.string().transform((v) => new Decimal(v)),
});
export const MiniProposal = TradeProposal.omit({
    trades: true,
    portfolioPlanAssignment: true,
}).extend({
    trades: MiniTrade.array(),
    portfolioModelName: z.string(),
    custodianAccountIds: CustodianAccountId.array(),
    portfolioPlanAssignmentStatus: ConcreteImplementationPlanStatus,
    portfolioPlanAssignmentIntent: ConcreteImplementationPlanIntent,
    clientIds: ClientId.array(),
    tradeProposalBundle: z
        .object({
        bundleStatus: TradeProposalBundleStatus,
        bundleId: TradeProposalBundleId,
    })
        .nullable(),
});
const ResponseSchema = z.object({
    data: MiniProposal.array(),
    pagination: PaginationMetadata,
});
export const url = (pagination) => {
    let qsString = "";
    if (pagination !== null) {
        const qs = new URLSearchParams();
        qs.append("page", pagination.page.toString());
        qs.append("pageSize", pagination.pageSize.toString());
        qs.append("orderBy", pagination.orderBy);
        if (pagination.expiresAtGt !== undefined) {
            qs.append("expiresAtGt", pagination.expiresAtGt instanceof Date
                ? formatISO(pagination.expiresAtGt)
                : pagination.expiresAtGt);
        }
        if (pagination.validFromLt !== undefined) {
            qs.append("validFromLt", pagination.validFromLt instanceof Date
                ? formatISO(pagination.validFromLt)
                : pagination.validFromLt);
        }
        if (pagination.createdAtGt !== undefined) {
            qs.append("createdAtGt", formatISO(pagination.createdAtGt));
        }
        if (pagination.createdAtLt !== undefined) {
            qs.append("createdAtLt", formatISO(pagination.createdAtLt));
        }
        if (pagination.custodianAccountId !== undefined) {
            qs.append("custodianAccountId", pagination.custodianAccountId);
        }
        // order the arrays first so we get deterministic ordering for caching
        if (pagination.bundleStatus !== undefined) {
            orderBy(pagination.bundleStatus).forEach((status) => {
                qs.append("bundleStatus", status);
            });
        }
        if (pagination.bundleStatusNot !== undefined) {
            orderBy(pagination.bundleStatusNot).forEach((status) => {
                qs.append("bundleStatusNot", status);
            });
        }
        if (pagination.proposalStatus !== undefined) {
            orderBy(pagination.proposalStatus).forEach((status) => {
                qs.append("proposalStatus", status);
            });
        }
        if (pagination.custodian !== undefined) {
            qs.append("custodian", pagination.custodian);
        }
        qsString = `?${qs.toString()}`; // notice leading `?`
    }
    return environment() === "PROD"
        ? `https://middleware.prod.farther.com/investment/trade-proposals${qsString}`
        : `https://middleware.uat.farther.com/investment/trade-proposals${qsString}`;
};
export const getProposals = async (pagination, config) => {
    const { data } = await axios.get(url(pagination), {
        headers: {
            version: "20241107",
            Authorization: `Bearer ${config.jwt}`,
            ...("advisorId" in config && { Advisor: config.advisorId }),
        },
    });
    const decoded = ResponseSchema.safeParse(data);
    if (decoded.success) {
        return decoded.data;
    }
    else {
        const errorMessage = `[getProposals] received unexpected data`;
        errorLog(errorMessage, decoded.error.message);
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
};
//# sourceMappingURL=getTradeProposals.js.map